import throttle from 'lodash/throttle';
import {
  createEvent,
  closest,
  addClass,
  removeClass,
  monitorInputValueChanges,
} from '@utils/domUtils';
import { stripWhitespace, stripTags } from '@utils/stringUtils';
import { getJobFormData } from '@utils/jobUtils';

/* global jQuery, KTUtil */

const MIN_SUCCESS_CONTENT_LENGTH = 15;

export default class JobEditSidebar {
  static selectorString() {
    return '.JobEditSidebar';
  }

  constructor() {
    this.throttledContentUpdate = throttle(this.updateContent, 150);
  }

  render(node, props) {
    this.form = closest(node, 'form');

    this.sections = [...node.querySelectorAll('.card-edit-block[data-section]')];
    this.sections.forEach(section => {
      section.sectionId = section.getAttribute('data-section');
      section.visibleCheckbox = section.querySelector('.visible-checkbox');
      section.enableBlockButton = section.querySelector('.enable-block');
      section.disableBlockButton = section.querySelector('.disable-block');
      section.blockDeactivatedMarker = section.querySelector('.block-deactivated-marker');
      section.blockEmptyMarker = section.querySelector('.block-empty-marker');
      section.blockSuccessMarker = section.querySelector('.block-success-marker');
      section.titleInput = section.querySelector(`[name="${section.sectionId}[title]"]`);
      section.descriptionTextarea = section.querySelector(
        `[name="${section.sectionId}[description]"]`,
      );
      section.wysiwyg = closest(section.descriptionTextarea, '.Wysiwyg');
    });

    this.attachmentsFileUpload = node.querySelector('[data-prop="attachments"] .FileUpload');
    this.attachmentsFileUpload.addEventListener('fileListUpdated', this.onContentChange.bind(this));

    const editBlocks = [...node.querySelectorAll('.card-edit-block')];
    editBlocks.forEach(block => {
      block.addEventListener('click', () => {
        jQuery(editBlocks)
          .not(block)
          .each((id, element) => {
            const card = KTUtil.data(element).get('card');
            if (card && card.collapse) {
              card.collapse();
            }
          });
      });
    });

    [...node.querySelectorAll('.enable-block a')].forEach(element => {
      element.addEventListener('click', this.enableBlockButtonClicked.bind(this));
    });

    [...node.querySelectorAll('.disable-block a')].forEach(element => {
      element.addEventListener('click', this.disableBlockButtonClicked.bind(this));
    });

    // force update on every key input. a bit hacky but works fine
    node.addEventListener('keyup', this.onContentChange.bind(this));
    node.querySelectorAll('input').forEach(element => {
      // monitor input value changes properly
      monitorInputValueChanges(element);
      element.addEventListener('value:changed', this.inputDataChanged.bind(this));
    });
    node.querySelectorAll('input, textarea, select').forEach(element => {
      element.addEventListener('change', this.inputDataChanged.bind(this));
    });

    this.updateBlockVisibilityMarkers();
  }

  enableBlockButtonClicked(event) {
    event.preventDefault();
    this.showSection(closest(event.currentTarget, '.card'));
  }

  disableBlockButtonClicked(event) {
    event.preventDefault();
    this.hideSection(closest(event.currentTarget, '.card'));
  }

  showSection(section) {
    if (section.visibleCheckbox) {
      section.visibleCheckbox.checked = true;
    }

    this.onContentChange();
  }

  hideSection(section) {
    if (section.visibleCheckbox) {
      section.visibleCheckbox.checked = false;
    }

    this.onContentChange();
  }

  updateBlockVisibilityMarkers() {
    this.sections.forEach(section => {
      const normalizedTextareaContent = stripWhitespace(
        stripTags(section.descriptionTextarea.value),
      );
      const normalizedTitleContent = stripWhitespace(section.titleInput.value);
      const contentLength = normalizedTitleContent.length + normalizedTextareaContent.length;

      // if title and description are empty:
      // visually tell user by showing block deactivated marker
      if (section.titleInput.value.length === 0 && section.descriptionTextarea.value.length === 0) {
        removeClass(section.blockEmptyMarker, 'hide');
      } else {
        addClass(section.blockEmptyMarker, 'hide');
      }

      if (section.visibleCheckbox.checked) {
        addClass(section.enableBlockButton, 'hide');
        removeClass(section.disableBlockButton, 'hide');
        addClass(section.blockDeactivatedMarker, 'hide');

        if (contentLength >= MIN_SUCCESS_CONTENT_LENGTH) {
          removeClass(section.blockSuccessMarker, 'hide');
        } else {
          addClass(section.blockSuccessMarker, 'hide');
        }
      } else {
        removeClass(section.enableBlockButton, 'hide');
        addClass(section.disableBlockButton, 'hide');
        removeClass(section.blockDeactivatedMarker, 'hide');
        addClass(section.blockSuccessMarker, 'hide');
      }
    });
  }

  inputDataChanged(event) {
    // TODO: store card / section in event's node and access directly
    // TODO: only call `showSection()` if it has a checkbox, otherwise call `onContentChange` directly
    this.showSection(closest(event.currentTarget, '.card'));
    this.onContentChange();
  }

  onContentChange() {
    this.throttledContentUpdate();
  }

  updateContent() {
    this.updateBlockVisibilityMarkers();
    this.node.dispatchEvent(createEvent('contentChanged'));
  }

  setSectionData(data) {
    Object.keys(data).forEach(sectionId => {
      const sectionData = data[sectionId];
      const section = this.sections.find(section => section.sectionId === sectionId);
      if (section) {
        section.titleInput.value = sectionData.title;
        section.descriptionTextarea.value = sectionData.description;
        section.wysiwyg.editor.setData(sectionData.description);
      }
    });

    this.updateBlockVisibilityMarkers();
    this.node.dispatchEvent(createEvent('contentChanged'));
  }

  getData() {
    const uploadedAttachments = this.attachmentsFileUpload.component
      ? this.attachmentsFileUpload.component.getUploadedFiles()
      : [];

    return {
      ...getJobFormData(
        this.form,
        this.sections.map(section => section.sectionId),
      ),
      ...{ uploadedAttachments: uploadedAttachments },
    };
  }
}
